import { Spinner, useViewportTier } from '@xantory/ui';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { Suspense } from 'preact/compat';
import { useLocation } from 'preact-iso/router';
import { useAuthStore, useConfigStore, canWith } from '@xantory/store';
import { useSites } from '@xantory/api';
import {
  buildRegistry,
  type FeatureManifest,
  overviewFeature,
  mapperFeature,
  devicesFeature,
  calibrationFeature,
  iotControlFeature,
  logisticsFeature,
  salesFeature,
  cockpitFeature,
  socFeature,
  assetsFeature,
  receivingFeature,
  locationsFeature,
  incidentsFeature,
  maintenanceFeature,
  workOrdersFeature,
  problemsFeature,
  warehouseFeature,
  procurementRequestsFeature,
  procurementApprovalsFeature,
  procurementOrdersFeature,
  procurementRulesFeature,
  usersFeature,
  usersRegistrationFeature,
  accessFeature,
  sitesFeature,
  masterDataFeature,
  cultivationFeature,
  planningAlertsFeature,
  productionPlanningFeature,
  itsmCommonsFeature,
  deviceCatalogFeature,
  fleetInventoryFeature,
  auditFeature,
  notificationsFeature,
  historyFeature,
  technicianMobileFeature,
  settingsFeature,
  authFlowsFeature,
  LoginPage,
  SiteSelectPage,
  type PermissionLevel,
} from '@xantory/features';
import { ROUTES, resourceHref } from '@xantory/features';
import { ShellLayout } from './ShellLayout';
import { MobileShellLayout } from './MobileShellLayout';
import { defaultRoute, FALLBACK_ROUTE } from './default-route';

// Sentinel is the master dashboard: it composes EVERY feature; per-user enablement lives in each
// manifest's `enabled` predicate (a future specific dashboard is a subset of this list).
// Nav section order follows first encounter, per the Sentinel_AppV2 prototype
// hierarchy (user call 2026-07-04): Farm ops first, Administration near the
// end, Setup at the very bottom (setup/config is not daily ops).
const FEATURES: FeatureManifest[] = [
  overviewFeature,
  fleetInventoryFeature,
  // HQ, third leaf: the standing cross-site notification record. Declared here
  // (not with the other account-ish screens) because array order IS the order
  // within a section — see the comment above.
  notificationsFeature,
  // MONITOR — declared right after the HQ leaves because array order IS section
  // order (buildRegistry creates a section the first time it is named), so this is
  // what puts Monitor between HQ and Farm in the sidebar. SOC + Cockpit live here
  // rather than in Farm: both are cross-cutting *watch* surfaces, not places you
  // operate a room from.
  socFeature,
  cockpitFeature,
  // FARM — Alerts leads the section (console R-357). Array order IS section
  // order, so this manifest has to be declared before `iotControlFeature` for
  // the leaf to sit first; its own file explains why it is a manifest at all.
  planningAlertsFeature,
  iotControlFeature,
  historyFeature,
  // ITSM section order (user-directed): Configuration first, operational
  // screens next, Problems & KB last. buildRegistry keeps per-section
  // declaration order, so this array order IS the sidebar order within ITSM.
  itsmCommonsFeature,
  assetsFeature,
  incidentsFeature,
  maintenanceFeature,
  // R-244: work orders sit BELOW Maintenance and beside it, not inside it — the
  // array order is the sidebar order within a section.
  workOrdersFeature,
  locationsFeature,
  problemsFeature,
  warehouseFeature,
  // PROCUREMENT — its own SECTION, and this array order IS both the section's
  // position in the sidebar and the order of its leaves (buildRegistry creates a
  // section the first time it is named, then appends items). One feature per
  // leaf is what makes the prototype's exact order expressible at all: nav
  // children merge append-only, so a shared group could never put Receiving 4th.
  //
  // Day-to-day order: Requests → My Approvals → Purchase Orders → Receiving,
  // with Approval Rules last because it is set up once and read rarely — even
  // though it has to EXIST first or nothing is ever reviewed.
  procurementRequestsFeature,
  procurementApprovalsFeature,
  procurementOrdersFeature,
  receivingFeature,
  procurementRulesFeature,
  logisticsFeature,
  salesFeature,
  usersFeature,
  usersRegistrationFeature,
  accessFeature,
  sitesFeature,
  masterDataFeature,
  cultivationFeature,
  productionPlanningFeature,
  deviceCatalogFeature,
  auditFeature,
  technicianMobileFeature,
  mapperFeature,
  devicesFeature,
  calibrationFeature,
  settingsFeature,
  authFlowsFeature,
];

// IoT Control (R-102) is now one page at `/farm/control` (left room panel + in-page detail). Fold
// every retired sub-path — the old scope routes (overview/grow/plumbing) and the short-lived
// per-level routes (room/section) — back to it so old links keep working.
function normalize(path: string): string {
  return path.startsWith('/farm/control/') ? '/farm/control' : path;
}

export function Router() {
  // preact-iso owns the URL (clean History-API paths, back/forward, link interception). We just read
  // the current path here and let the feature registry decide the screen + sidebar + breadcrumbs.
  const loc = useLocation();
  const path = normalize(loc.path);
  const query = useMemo(() => new URLSearchParams(loc.query), [loc.query]);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const addingAccount = useAuthStore((s) => s.addingAccount);
  const activeId = useAuthStore((s) => s.activeId);
  const userType = useAuthStore((s) => s.user?.userType);
  const permissions = useAuthStore((s) => s.user?.permissions);
  const mode = useConfigStore((s) => s.mode);
  // Viewport tier for the MD-2 mobile landing preference (R-147).
  const tier = useViewportTier();
  const siteId = useConfigStore((s) => s.siteId);
  const setSite = useConfigStore((s) => s.setSite);
  const clearSite = useConfigStore((s) => s.clearSite);

  // Routes + sidebar + breadcrumbs all derive from the one manifest registry, re-resolved when the
  // enablement inputs (mode, user type, permission set) change — no second copy of "what this user
  // may see". `can` is UX gating only; the gateways enforce (R-058/D-061).
  const registry = useMemo(() => {
    const can = (module: string, min: PermissionLevel) => canWith(userType, permissions, module, min);
    return buildRegistry(FEATURES, { mode, userType, can });
  }, [mode, userType, permissions]);

  // Validate a persisted cloud-site selection against the live registry. A site can be removed or
  // renamed (or be a stale mock id from a pre-live build) — left unchecked, the gate below would wave
  // the stale id through and every `/sites/{id}` call 404s with no way back to the picker. Only clear
  // when the list has actually LOADED and the id is genuinely absent — never on loading/error, so an
  // offline reload keeps the (still-valid) cached selection (D-037 offline-first).
  const siteList = useSites(mode === 'cloud' && isAuthenticated);
  const homeSiteId = useAuthStore((st) => st.user?.homeSiteId);
  useEffect(() => {
    if (mode === 'cloud' && siteId && siteList.data && !siteList.data.sites.some((s) => s.id === siteId)) {
      clearSite();
    }
  }, [mode, siteId, siteList.data, clearSite]);

  // No site picked yet (first login) → default to a site; the top-bar switcher
  // changes it later. The manual picker only remains for the no-sites/error edge
  // (user call 2026-07-04 — replaces the login-time gate).
  //
  // ⚠ Somebody homed to a farm defaults to THEIR farm, not to whatever sorts
  // first. Picking the wrong one 403s every site-scoped call (`infra_admission`
  // admits an infra token only for an admin or the site's own people), which
  // surfaces as screens that quietly render empty rather than as an auth error.
  useEffect(() => {
    if (mode === 'cloud' && !siteId && siteList.data?.sites.length) {
      const sites = siteList.data.sites;
      const pick = (homeSiteId && sites.find((s) => s.id === homeSiteId)) || sites[0]!;
      setSite(pick.id, pick.name);
    }
  }, [mode, siteId, siteList.data, setSite, homeSiteId]);

  // Fresh-login one-shot (R-147 follow-up): the persistent home (bare `/`,
  // unknown paths, the sidebar logo) is the stable per-mode base — Overview on
  // cloud — but the FIRST destination right after authenticating should still
  // be role-based (SOC agent → /soc, technician → cockpit/incidents; MD-2: a
  // phone-sized local maintenance tech → mobile My-Work). Detect the
  // not-authed → authed transition and navigate once. A session restored from
  // storage on reload starts already authed, so this never fires then — and an
  // explicit deep link the user was headed to is preserved (only the
  // no-destination root/unknown case is steered to the role landing).
  const wasAuthed = useRef(isAuthenticated);
  useEffect(() => {
    const prev = wasAuthed.current;
    wasAuthed.current = isAuthenticated;
    if (prev || !isAuthenticated) return;
    const here = normalize(loc.path);
    if (here !== ROUTES.home && registry.knownRoutes.includes(here)) return;
    const can = (module: string, min: PermissionLevel) => canWith(userType, permissions, module, min);
    const landing = defaultRoute(mode, { can });
    const target =
      tier === 'narrow' && mode === 'local' && can('maintenance', 'write') ? ROUTES.mWork : landing;
    loc.route(target, true);
  }, [isAuthenticated, mode, tier, userType, permissions, registry, loc]);

  // R-303: switching accounts changes what the operator may see — the registry is
  // rebuilt from the new user's permissions, so a route the previous account could
  // open may simply not exist for this one. Without this they land on a dead page
  // immediately after switching, which reads as the switch having broken something.
  const lastActiveId = useRef(activeId);
  useEffect(() => {
    const prev = lastActiveId.current;
    lastActiveId.current = activeId;
    if (!activeId || prev === activeId || prev === null) return;
    const here = normalize(loc.path);
    if (registry.knownRoutes.includes(here)) return;
    const can = (module: string, min: PermissionLevel) => canWith(userType, permissions, module, min);
    loc.route(defaultRoute(mode, { can }), true);
  }, [activeId, mode, userType, permissions, registry, loc]);

  // Legacy → canonical URL redirects (assets restructure 2026-07-18; printed QR
  // labels embed the old /assets/record URL, so these must keep working).
  const legacyTarget = useMemo(() => {
    if (path === '/assets') {
      const qs = query.toString();
      return qs ? `${ROUTES.assets}?${qs}` : ROUTES.assets;
    }
    // Stockroom is merged into the register (R-147): the in-stock filter IS the
    // stockroom view. Old /assets/stockroom links land on the register filtered
    // to stock (which reveals the substatus chips + pick order).
    if (path === ROUTES.assetsStockroom) return `${ROUTES.assets}?state=stock`;
    if (path === '/assets/record') {
      const qs = query.toString();
      return qs ? `${ROUTES.assetsRecord}?${qs}` : ROUTES.assetsRecord;
    }
    // R-234: Procurement became a nav group with a leaf per step of the chain,
    // and Receiving moved out of ITSM into it. `/procurement` was the single
    // tabbed page and is the likeliest live bookmark; `?id=` on it addressed a
    // requisition row, which is now a record page of its own.
    if (path === '/procurement') {
      const id = query.get('id');
      return id
        ? `${ROUTES.procurementRequestRecord}?id=${encodeURIComponent(id)}`
        : ROUTES.procurementRequests;
    }
    if (path === ROUTES.assetsReceiving) return ROUTES.procurementReceiving;
    if (path === '/maintenance') return ROUTES.maintenanceIncidents;
    // ⚠ `/production`, `/production/demands`, `/production/batches` and
    // `/trace` / `/workforce` / `/ai` fold forward to NOTHING now — those
    // features were removed and there is no honest destination for a stale
    // link, so they fall through to the 404 rather than being redirected to a
    // screen that does not answer the question. Production PLANNING kept its
    // own `/planning/*` paths and is unaffected.
    // R-204: Cultivation became a group; crops/varieties left Master Data for it.
    // R-210: and its first three leaves became ONE Catalogue tree, so all three
    // retired paths fold forward. `/crops` and `/varieties` existed for two days
    // and were the group's landing page, so they are the likeliest live bookmarks.
    if (
      path === '/admin/cultivation/crops' ||
      path === '/admin/cultivation/varieties' ||
      path === '/admin/cultivation/categories'
    )
      return ROUTES.cultivationCatalogue;
    // R-330: the base path is the nav leaf's key, not a route, so it still needs a
    // destination — and it is now the LANDING TAB (Stages) rather than Catalogue.
    // The three paths above keep folding to Catalogue because they were the crop
    // TABLES that became that tree; the bare base was only ever the group's
    // landing page, which is what this leaf's `href` now is.
    if (path === ROUTES.cultivation) return ROUTES.cultivationStages;
    // R-200: the resolver a notification EMAIL links to. The mail is rendered
    // server-side and cannot call `resourceHref`, so rather than re-implementing
    // that mapping in Rust — where it would drift out of step invisibly until an
    // operator clicked a dead link — the email points here with the raw
    // (type, id) and this hands them to the one function that owns the mapping.
    //
    // Unresolvable types land on the inbox rather than a blank screen: the
    // notification is always there, so the click is never wasted.
    if (path === '/r') {
      return resourceHref(query.get('type'), query.get('id')) ?? ROUTES.notifications;
    }
    return null;
  }, [path, query]);
  useEffect(() => {
    if (legacyTarget) loc.route(legacyTarget, true);
  }, [legacyTarget, loc]);
  if (legacyTarget) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  // Public routes — shown regardless of auth state, outside the shell. Lazy-loaded, so a full-screen
  // spinner covers the chunk load.
  if (registry.isPublic(path))
    return (
      <Suspense
        fallback={
          <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
            <Spinner size="lg" />
          </div>
        }
      >
        {registry.render(path, query)}
      </Suspense>
    );

  // R-303: `addingAccount` shows the login screen OVER a live session so a second
  // operator can sign in without the first being signed out. `setSession` clears the
  // flag, so completing the form lands straight back in the console.
  if (addingAccount || !isAuthenticated) return <LoginPage />;

  // Cloud mode addresses a specific site (`/sites/{id}`), so SITE-SCOPED routes need a selection.
  // Cloud-GLOBAL routes (admin/config under `/admin/*`, the fleet overview/inventory, account
  // pages, cloud-tier Problems/KB) call cloud-tier endpoints directly and need NO site — they must
  // render even with zero sites, or an admin could never reach Administration → Sites to create the
  // first one (R-150). The manual picker only gates the site-scoped routes.
  const siteAgnostic =
    path.startsWith('/admin/') ||
    path.startsWith('/fleet/') ||
    path === ROUTES.overview ||
    path === ROUTES.settings ||
    path === ROUTES.notifications ||
    path === ROUTES.problems ||
    path === ROUTES.knowledgeBase;
  if (mode === 'cloud' && !siteId && !siteAgnostic) {
    if (siteList.isLoading) {
      return (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
          <Spinner size="lg" />
        </div>
      );
    }
    return <SiteSelectPage />;
  }

  // Unknown paths AND routes of disabled features (e.g. admin pages for a member) fall back to the
  // permission-derived landing (R-146). Guard it against the live registry: a permission mix whose
  // preferred landing isn't an enabled route drops back to the per-mode base fallback rather than
  // rendering a blank screen.
  // The persistent "home" — bare `/`, unknown paths and the sidebar logo — is
  // the stable per-mode base (cloud → Overview; R-147 follow-up). The
  // role-based landing is a one-shot on fresh login only (effect above), not
  // the resting home. Guard against a base that isn't an enabled route.
  const homeRoute = registry.knownRoutes.includes(FALLBACK_ROUTE[mode])
    ? FALLBACK_ROUTE[mode]
    : (registry.knownRoutes[0] ?? FALLBACK_ROUTE[mode]);
  const activeRoute = registry.knownRoutes.includes(path) ? path : homeRoute;
  // The nav highlight matches on path+query so query-keyed entries (Stockroom =
  // /assets?state=stock) light up instead of their base route (longest match wins).
  const qs = query.toString();
  const activeNavKey = qs ? `${activeRoute}?${qs}` : activeRoute;

  // R-147: `/m/*` routes render inside the mobile technician shell (bottom
  // nav); everything else keeps the desktop ShellLayout. Boundary-safe prefix
  // match — `/maintenance` also starts with "/m".
  if (activeRoute === ROUTES.mHome || activeRoute.startsWith(`${ROUTES.mHome}/`)) {
    return (
      <MobileShellLayout activeRoute={activeRoute}>{registry.render(activeRoute, query)}</MobileShellLayout>
    );
  }

  return (
    <ShellLayout
      activeRoute={activeNavKey}
      navSections={registry.navSections}
      breadcrumb={registry.breadcrumb(activeRoute)}
    >
      {registry.render(activeRoute, query)}
    </ShellLayout>
  );
}
